import { getData, postData, putData } from "actions";
import { CKEditor } from "ckeditor4-react";
import { CardContainer, Container } from "components";
import Breadcrumb from "components/molecules/Breadcrumb";
import { API_URL_kbCategory } from "constants/api";
import { useFormik } from "formik";
import * as Yup from "yup";
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useContext,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { kbCategoryReducers } from "redux/kbCategorySlice";
import { AuthContext } from "context/AuthContext";

const KBFormCategory = ({ typeForm }) => {
  const location = useLocation();
  const [adminAccess, setAdminAccess] = useState(false);
  const { datauser } = useContext(AuthContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const inputRefs = useRef([]);

  const formik = useFormik({
    initialValues: {
      name: location.state ? location.state.item.name : "",
      status: location.state ? location.state.item.status : 0,
      description: location.state ? location.state.item.description : false,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
    }),
    onSubmit: (values) => {
      const formData = values;
      if (location.state === null) {
        postData(
          API_URL_kbCategory,
          formData,
          { dispatch, redux: kbCategoryReducers },
          "ADD_KBCATEGORY",
          () => {
            navigate("/administrator/knowledgebase/category");
          }
        );
      } else {
        putData(
          `${API_URL_kbCategory}${location.state.item.id}/`,
          formData,
          { dispatch, redux: kbCategoryReducers },
          "ADD_KBCATEGORY",
          () => {
            navigate("/administrator/knowledgebase/category");
          }
        );
      }
    },
  });

  const getKbCategory = useCallback(
    async (params) => {
      getData(
        API_URL_kbCategory,
        params,
        { dispatch, redux: kbCategoryReducers },
        "GET_KBCATEGORY"
      );
    },
    [dispatch]
  );

  // Focus Input validate & submit
  const handleFormSubmit = async (event) => {
    event.preventDefault();
    await formik.validateForm();
    const firstInvalidInputRef = inputRefs.current.find((inputRef) => {
      const fieldName = inputRef.name;
      formik.setFieldTouched(fieldName, true, true);
      return formik.errors[fieldName];
    });
    if (firstInvalidInputRef) {
      firstInvalidInputRef.focus();
    } else {
      formik.handleSubmit();
    }
  };

  const toolbarConfig = {
    extraPlugins: "font,colorbutton,colordialog",
    removePlugins: "about,image",
    skin: "moono",
  };

  useEffect(() => {
    if (typeForm === "create") {
      formik.setFieldValue("name", "");
      formik.setFieldValue("status", 0);
      formik.setFieldValue("parent", "");
      formik.setFieldValue("description", "");
    }
  }, [typeForm]);

  useEffect(() => {
    const params = `?limit=${9999999}`;
    getKbCategory(params);
  }, []);

  useEffect(() => {
    if (datauser !== "") {
      const permissionViewMenuAdmin = datauser.group_permissions.includes(
        "access_menu_admin"
      );
      if (permissionViewMenuAdmin) {
        setAdminAccess(true);
      } else {
        navigate("/dashboard/agent");
      }
    }
  }, [datauser]);

  if (!adminAccess) {
    return null;
  }

  return (
    <Container>
      <Breadcrumb
        title={"Form Category"}
        excludedPaths={["knowledgebase", "administrator"]}
        group={"Superadmin"}
      />
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12">
          <CardContainer
            title={typeForm === "create" ? "Create Category" : "Edit Category"}
          >
            <form onSubmit={handleFormSubmit}>
              <div className="m-5 flex flex-col md:flex-row gap-2">
                <div className="flex flex-col">
                  <label>
                    Name
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[0] = ref)}
                    value={formik.values.name}
                    className="p-1 w-full lg:w-64 border border-gray-300 dark:text-black focus:border-blue-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  />
                  {formik.touched.name && formik.errors.name && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.name}
                    </div>
                  )}
                </div>

                <div className="md:mx-4 py-2 align-middle items-center">
                  <label className="block">Status</label>
                  <label className="inline-flex items-center ml-1">
                    <input
                      id="status"
                      name="status"
                      type="radio"
                      checked={formik.values.status === 1}
                      onChange={() => formik.setFieldValue("status", 1)}
                      ref={(ref) => (inputRefs.current[1] = ref)}
                      className="w-4 h-4 rounded-full"
                    />
                    <span className="ml-2">Active</span>
                  </label>
                  <label className="inline-flex items-center ml-1">
                    <input
                      id="status"
                      name="status"
                      type="radio"
                      checked={formik.values.status === 0}
                      onChange={() => formik.setFieldValue("status", 0)}
                      ref={(ref) => (inputRefs.current[2] = ref)}
                      className="lg:ml-2 w-4 h-4 rounded-full"
                    />
                    <span className="ml-2">Non Active</span>
                  </label>
                </div>
              </div>

              <div className="m-5 flex flex-col gap-2">
                <label>
                  Description
                  <span className="text-red-500">*</span>
                </label>

                <CKEditor
                  config={toolbarConfig}
                  type="classic"
                  initData={formik.values.description}
                  onChange={(e) => {
                    formik.setFieldValue("description", e.editor.getData());
                  }}
                />
              </div>
              <div className="h-14 w-full flex items-center px-5 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600">
                <button
                  type="submit"
                  className="rounded-md text-white py-1.5 w-16 bg-sky-600 hover:bg-sky-800 "
                >
                  Submit
                </button>
              </div>
            </form>
          </CardContainer>
        </div>
      </div>
    </Container>
  );
};

export default KBFormCategory;
